import { Component } from '@theme/component';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class ContentTabsComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    super.connectedCallback();

    const tablist = /** @type {HTMLElement | null} */ (this.querySelector('.tab-buttons'));
    this.#panels = Array.from(this.querySelectorAll('.tab-content'));

    if (!tablist || this.#panels.length === 0) return;

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    let activeIndex = this.#panels.findIndex((p) => p.dataset.openByDefault === 'true');
    if (activeIndex === -1) activeIndex = 0;

    this.#tabs = this.#panels.map((panel, i) => this.#createTab(panel, i, activeIndex, opts));
    tablist.replaceChildren(...this.#tabs);

    this.#applyActiveState(activeIndex);

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, panel] of this.#panels.entries()) {
      panel.addEventListener('keydown', (e) => this.#handlePanelKeydown(e, i), opts);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} index
   * @param {number} activeIndex
   * @param {AddEventListenerOptions & { signal: AbortSignal }} opts
   */
  #createTab(panel, index, activeIndex, opts) {
    const isActive = index === activeIndex;
    const heading = panel.dataset.heading || 'Tab';
    const panelId = panel.id;
    const tabId = panelId ? `Tab-${panelId.replace(/^Panel-/, '')}` : `Tab-${index}`;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `tab-btn${isActive ? ' active' : ''}`;
    btn.id = tabId;
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-controls', panelId);
    btn.setAttribute('aria-selected', String(isActive));
    btn.setAttribute('tabindex', isActive ? '0' : '-1');
    btn.dataset.tab = panelId;

    const label = document.createElement('span');
    label.className = 'tab-btn__label';
    label.textContent = heading;
    btn.appendChild(label);

    panel.setAttribute('aria-labelledby', tabId);

    btn.addEventListener('click', () => this.#activate(index), opts);
    btn.addEventListener('focus', () => {
      if (btn.matches(':focus-visible')) this.#activate(index);
    }, opts);

    return btn;
  }

  /** @param {number} index */
  #activate(index) {
    if (index < 0 || index >= this.#tabs.length) return;
    this.#applyActiveState(index);
  }

  /** @param {number} index */
  #applyActiveState(index) {
    for (const [i, tab] of this.#tabs.entries()) {
      const isActive = i === index;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of this.#panels.entries()) {
      const isActive = i === index;
      panel.classList.toggle('active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      panel.toggleAttribute('inert', !isActive);
    }
  }

  /** @param {KeyboardEvent} e */
  #handleKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (!target.classList.contains('tab-btn')) return;

    const i = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (i === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: this.#tabs.length - 1 - i,
    };

    const offset = navMap[/** @type {keyof typeof navMap} */ (e.key)];
    if (offset === undefined) return;

    e.preventDefault();
    const next = (i + offset + this.#tabs.length) % this.#tabs.length;
    this.#tabs[next]?.focus();
    this.#activate(next);
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handlePanelKeydown(event, index) {
    if (event.key !== 'Tab') return;

    const panel = /** @type {HTMLElement} */ (event.currentTarget);
    const focusable = this.#getFocusableElements(panel);
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (event.shiftKey) {
      const atStart =
        (firstFocusable && document.activeElement === firstFocusable) ||
        (!focusable.length && document.activeElement === panel);
      if (atStart && index > 0) {
        event.preventDefault();
        this.#activate(index - 1);
        this.#focusPanelEdge(index - 1, 'end');
      }
      return;
    }

    const atEnd =
      (lastFocusable && document.activeElement === lastFocusable) ||
      (!focusable.length && document.activeElement === panel);

    if (atEnd && index < this.#panels.length - 1) {
      event.preventDefault();
      this.#activate(index + 1);
      this.#focusPanelEdge(index + 1, 'start');
    }
  }

  /**
   * @param {number} index
   * @param {'start' | 'end'} [position]
   */
  #focusPanelEdge(index, position = 'start') {
    const panel = this.#panels[index];
    if (!panel) return;

    const focusable = this.#getFocusableElements(panel);
    const target = position === 'end' ? focusable[focusable.length - 1] : focusable[0];

    requestAnimationFrame(() => (target ?? panel).focus());
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement[]}
   */
  #getFocusableElements(panel) {
    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => !el.closest('[inert]') && !el.closest('[hidden]'))
      .map((el) => /** @type {HTMLElement} */ (el));
  }
}

if (!customElements.get('content-tabs-component')) {
  customElements.define('content-tabs-component', ContentTabsComponent);
}
